import { useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

/**
 * Build an in-app link under whichever route tree the caller is rendered in.
 *
 * Application routes live at /t/:tenantId/* and, for superadmin, /platform/*.
 * A bare <Link to="/plan-usage"> reaches neither: it falls through every named
 * route to the /:agentName public-demo catch-all in App.jsx. Several links
 * were quietly broken that way.
 *
 * The prefix is taken from the URL rather than from the user's role, because
 * the URL is what actually says which tree this render belongs to -- a
 * superadmin browsing /t/:tenantId/* must keep linking inside that tenant, and
 * the same superadmin on /platform/* must stay on the platform pages. Using
 * the role instead would send a superadmin viewing /platform/plan-usage to a
 * tenant path they have no tenant id for, and TenantRedirect would bounce them
 * out to the login screen.
 *
 * Mirrors Sidebar.jsx::buildPath. If the two ever disagree, the sidebar entry
 * and the in-page link for the same destination will point to different places.
 */
export const useTenantPath = () => {
  const { pathname } = useLocation();
  const { tenantId } = useParams();
  const user = useAuthStore((s) => s.user);

  if (pathname === '/platform' || pathname.startsWith('/platform/')) {
    return (path) => `/platform${path}`;
  }

  const base = tenantId || user?.tenant_id;
  // No tenant at all: return the bare path, which App.jsx routes through
  // TenantRedirect once a tenant is known.
  return (path) => (base ? `/t/${base}${path}` : path);
};

export default useTenantPath;
